/**
 * Стороны стен, смотрящие в комнату, — то, чему кнопка «на всю комнату» назначает отделку.
 *
 * Комната знает свои стены (`room.wallIds`) и чистый контур (`room.inner`), но не знает,
 * какой стороной каждая стена к ней повёрнута. Сторона считается так же, как в развёртках:
 * пробная точка от середины ребра чистого контура показывает, где комната, а знак
 * проекции левой нормали стены на это направление — сторону (1 или −1, как `Fixture.side`).
 */
import {
	add,
	dot,
	leftNormal,
	mul,
	norm,
	pointInPolygon,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { openingsOfWall, type WallFace, type WallGraph } from "@/shared/plan";
import type { Room } from "./rooms";

/** Насколько от ребра отступает пробная точка, мм. */
const PROBE = 20;

/** Все стороны стен комнаты в порядке обхода её контура; одна стена — один раз. */
export const roomWallFaces = (plan: WallGraph, room: Room): WallFace[] => {
	const faces: WallFace[] = [];
	const seen = new Set<string>();
	const count = room.inner.length;
	for (let i = 0; i < count; i++) {
		const wall = plan.walls[room.wallIds[i]];
		const a = wall && plan.nodes[wall.a];
		const b = wall && plan.nodes[wall.b];
		if (!wall || !a || !b || seen.has(wall.id)) continue;
		const from = room.inner[i];
		const to = room.inner[(i + 1) % count];
		const edge = sub(to, from);
		if (Math.hypot(edge.x, edge.y) < 1) continue;
		const candidate = leftNormal(norm(edge));
		const middle: Pt = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
		const inward = pointInPolygon(add(middle, mul(candidate, PROBE)), room.inner)
			? candidate
			: mul(candidate, -1);
		seen.add(wall.id);
		faces.push({ wallId: wall.id, side: dot(leftNormal(norm(sub(b, a))), inward) >= 0 ? 1 : -1 });
	}
	return faces;
};

/**
 * Сторона стены под точкой: знак отступа точки от оси a → b по левой нормали.
 * Точка — в координатах плана, мм: в 3D её сначала переводят из сцены в план.
 */
export const wallSideAt = (a: Pt, b: Pt, point: Pt): 1 | -1 =>
	dot(leftNormal(sub(b, a)), sub(point, a)) >= 0 ? 1 : -1;

/** Комната, в которую смотрит сторона стены; null — снаружи квартиры. */
export const roomOfFace = (plan: WallGraph, rooms: Room[], face: WallFace): Room | null =>
	rooms.find((room) =>
		roomWallFaces(plan, room).some((f) => f.wallId === face.wallId && f.side === face.side),
	) ?? null;

/** Сторона стены в комнате и участок стены, где проходит чистая грань комнаты. */
export interface RoomFaceSpan extends WallFace {
	/** Начало и конец грани — отступы от начала стены (узла `a`), мм; `from` ≤ `to` */
	from: number;
	to: number;
}

/**
 * Стороны стен комнаты с участками, где они смотрят в комнату. Грань короче оси стены:
 * в углу её съедают примыкающие стены. Предмет на стене держится в этих пределах —
 * иначе кондиционер у угла вылезает в соседнюю стену.
 */
export const roomFaceSpans = (plan: WallGraph, room: Room): RoomFaceSpan[] => {
	const spans: RoomFaceSpan[] = [];
	const count = room.inner.length;
	for (let i = 0; i < count; i++) {
		const wall = plan.walls[room.wallIds[i]];
		const a = wall && plan.nodes[wall.a];
		const b = wall && plan.nodes[wall.b];
		if (!wall || !a || !b) continue;
		const from = room.inner[i];
		const to = room.inner[(i + 1) % count];
		const edge = sub(to, from);
		if (Math.hypot(edge.x, edge.y) < 1) continue;
		const candidate = leftNormal(norm(edge));
		const middle: Pt = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
		const inward = pointInPolygon(add(middle, mul(candidate, PROBE)), room.inner)
			? candidate
			: mul(candidate, -1);
		const along = norm(sub(b, a));
		const start = dot(sub(from, a), along);
		const end = dot(sub(to, a), along);
		spans.push({
			wallId: wall.id,
			side: dot(leftNormal(along), inward) >= 0 ? 1 : -1,
			from: Math.min(start, end),
			to: Math.max(start, end),
		});
	}
	return spans;
};

export const roomBaseboardSpans = (plan: WallGraph, room: Room): RoomFaceSpan[] =>
	roomFaceSpans(plan, room).flatMap((span) => {
		const segments: RoomFaceSpan[] = [];
		const doors = openingsOfWall(plan, span.wallId)
			.filter((opening) => opening.kind === "door" && opening.width > 0)
			.toSorted(
				(first, second) => first.offset - first.width / 2 - (second.offset - second.width / 2),
			);
		let cursor = span.from;
		for (const opening of doors) {
			const from = Math.max(span.from, opening.offset - opening.width / 2);
			const to = Math.min(span.to, opening.offset + opening.width / 2);
			if (to <= cursor || from >= span.to) continue;
			if (from > cursor) segments.push({ ...span, from: cursor, to: from });
			cursor = Math.max(cursor, to);
		}
		if (cursor < span.to) segments.push({ ...span, from: cursor });
		return segments;
	});
